
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface NotificationAnalytics {
  date: string;
  notification_type: string;
  total_sent: number;
  total_delivered: number;
  total_failed: number;
  total_read: number;
  avg_read_time_minutes: number;
}

export interface EmailLog {
  id: string;
  notification_id: string;
  email_address: string;
  status: string;
  provider_id?: string;
  sent_at?: string;
  delivered_at?: string;
  error_message?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export const useNotificationAnalytics = () => {
  const [analytics, setAnalytics] = useState<NotificationAnalytics[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAnalytics = async (days: number = 30) => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('notification_analytics')
        .select('*')
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      if (error) throw error;

      setAnalytics(data || []);
    } catch (error) {
      console.error('Error fetching notification analytics:', error);
    }
  };

  const fetchEmailLogs = async (limit: number = 100) => {
    try {
      const { data, error } = await supabase
        .from('notification_email_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      setEmailLogs(data || []);
    } catch (error) {
      console.error('Error fetching email logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAnalyticsSummary = () => {
    const summary = analytics.reduce(
      (acc, item) => ({
        totalSent: acc.totalSent + item.total_sent,
        totalDelivered: acc.totalDelivered + item.total_delivered,
        totalFailed: acc.totalFailed + item.total_failed,
        totalRead: acc.totalRead + item.total_read,
      }),
      { totalSent: 0, totalDelivered: 0, totalFailed: 0, totalRead: 0 }
    );

    return {
      ...summary,
      deliveryRate: summary.totalSent > 0 ? (summary.totalDelivered / summary.totalSent) * 100 : 0,
      readRate: summary.totalDelivered > 0 ? (summary.totalRead / summary.totalDelivered) * 100 : 0,
      failureRate: summary.totalSent > 0 ? (summary.totalFailed / summary.totalSent) * 100 : 0,
    };
  };

  const getNotificationTypeBreakdown = () => {
    const breakdown = analytics.reduce((acc, item) => {
      if (!acc[item.notification_type]) {
        acc[item.notification_type] = {
          sent: 0,
          delivered: 0,
          failed: 0,
          read: 0,
        };
      }
      acc[item.notification_type].sent += item.total_sent;
      acc[item.notification_type].delivered += item.total_delivered;
      acc[item.notification_type].failed += item.total_failed;
      acc[item.notification_type].read += item.total_read;
      return acc;
    }, {} as Record<string, { sent: number; delivered: number; failed: number; read: number }>);

    return Object.entries(breakdown).map(([type, stats]) => ({
      type,
      ...stats,
      deliveryRate: stats.sent > 0 ? (stats.delivered / stats.sent) * 100 : 0,
      readRate: stats.delivered > 0 ? (stats.read / stats.delivered) * 100 : 0,
    }));
  };

  useEffect(() => {
    fetchAnalytics();
    fetchEmailLogs();
  }, []);

  return {
    analytics,
    emailLogs,
    isLoading,
    fetchAnalytics,
    fetchEmailLogs,
    getAnalyticsSummary,
    getNotificationTypeBreakdown,
  };
};
